// Rebaseline Review Card — visual view of /admin/grading-rebaseline/report
// (live v1 vs shadow v2 grading deltas). Dark admin-console styling.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, FlaskConical, RefreshCw } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, tone = 'text-white' }) => (
  <div className="bg-white/5 rounded-lg px-3 py-2 text-center">
    <div className={`text-lg font-bold ${tone}`}>{value}</div>
    <div className="text-[10px] text-white/40 uppercase tracking-wider">{label}</div>
  </div>
);

const RebaselineReviewCard = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/grading-rebaseline/report`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const all = data?.domains || [];
  const totals = data?.total_shadowed || 0;
  const meanAbs = all.length ? Math.max(...all.map((d) => d.mean_abs_delta)) : 0;
  const flips = all.reduce((s, d) => s + (d.queue_flips_at_70 || 0), 0);
  const verdict = totals === 0 ? null : (meanAbs <= 1 && flips === 0 ? 'ok' : 'watch');

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden" data-testid="rebaseline-review-card">
      <div className="px-5 py-3.5 flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-teal-400" />
          <div>
            <h3 className="text-sm font-bold text-white">Caching Re-Baseline — shadow grading</h3>
            <p className="text-[10px] text-white/35">Live v1 vs frozen v2 layout · shadow flag: {data?.shadow_flag || 'off'} · cutover: {data?.cutover_flag_prompt_cache_domains || 'OFF'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {verdict === 'ok' && <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300" data-testid="rebaseline-verdict">NO DEGRADATION</span>}
          {verdict === 'watch' && <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-amber-500/15 text-amber-300" data-testid="rebaseline-verdict">REVIEW DELTAS</span>}
          <button onClick={load} className="text-white/30 hover:text-white/70" data-testid="rebaseline-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="p-5">
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-white/30" /></div>
        ) : totals === 0 ? (
          <p className="text-xs text-white/40">No shadow pairs collected yet — they accrue as new calls are graded while the shadow flag is on.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-2">
              <Stat label="Calls shadowed" value={totals} />
              <Stat label="Worst mean |Δ|" value={meanAbs} tone={meanAbs <= 1 ? 'text-emerald-300' : 'text-amber-300'} />
              <Stat label="Queue flips @70" value={flips} tone={flips === 0 ? 'text-emerald-300' : 'text-red-300'} />
              <Stat label="Within ±1pt" value={all.reduce((s, d) => s + (d.within_1pt || 0), 0)} />
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-white/30 uppercase text-[9px] tracking-wider border-b border-white/5">
                  <th className="py-1.5">Domain</th><th className="py-1.5 text-right">n</th>
                  <th className="py-1.5 text-right">Mean |Δ|</th><th className="py-1.5 text-right">Max |Δ|</th>
                  <th className="py-1.5 text-right">&gt;3pt</th><th className="py-1.5 text-right">Flips</th>
                </tr>
              </thead>
              <tbody>
                {all.map((d) => (
                  <tr key={d.domain_id} className="border-b border-white/5 text-white/70" data-testid={`rebaseline-domain-${d.domain_id}`}>
                    <td className="py-1.5 font-mono text-[10px]">{d.domain_id?.slice(0, 8)}…</td>
                    <td className="py-1.5 text-right">{d.n}</td>
                    <td className="py-1.5 text-right font-semibold">{d.mean_abs_delta}</td>
                    <td className="py-1.5 text-right">{d.max_abs_delta}</td>
                    <td className="py-1.5 text-right">{d.over_3pt}</td>
                    <td className={`py-1.5 text-right ${d.queue_flips_at_70 ? 'text-red-300' : ''}`}>{d.queue_flips_at_70}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(data?.agents_most_affected || []).slice(0, 3).filter((a) => a.mean_abs_delta > 0).length > 0 && (
              <p className="text-[10px] text-white/35">
                Most affected: {(data.agents_most_affected || []).slice(0, 3).map((a) => `${a.agent_name || a.agent_id} (|Δ| ${a.mean_abs_delta})`).join(' · ')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RebaselineReviewCard;
